import {
  BadgeQuestionMark,
  BarChart3,
  Book,
  Briefcase,
  ClipboardList,
  Building,
  Building2,
  Activity,
  Calendar,
  Check,
  FileText,
  GraduationCap,
  GroupIcon,
  Handshake,
  Home,
  LogOut,
  Medal,
  Menu,
  MessageCircle,
  UploadCloud,
  User,
  Users,
  UsersRound,
  X,
  Receipt,
  ChevronLeft,
  ChevronRight,
  Route,
} from "lucide-react";

import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { trainerApi, Trainer } from "../api";
import logoImage from "../assets/logo.png";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3001/api";
const BACKEND_BASE = API_BASE.replace(/\/api\/?$/, "");

const setAuthCookieForBackend = () => {
  const token = localStorage.getItem("auth_token");
  if (!token) return;
  try {
    // Bull board plugin accepts either auth_token or token cookie and lifts it to Authorization
    document.cookie = `auth_token=${token}; path=/; SameSite=Lax`;
    document.cookie = `token=${token}; path=/; SameSite=Lax`;
  } catch {
    // ignore cookie write errors
  }
};

const buildQueuesUrlWithToken = (path: string) => {
  if (!path.includes("/admin/queues")) return path;
  try {
    const url = new URL(path);
    const token = localStorage.getItem("auth_token");
    if (token) {
      url.searchParams.set("token", token);
    }
    return url.toString();
  } catch {
    return path;
  }
};

export interface SidebarItem {
  name: string;
  path: string;
  icon: React.ElementType;
  badge?: string;
  adminOnly?: boolean;
  allowed_roles?: string[];
  description?: string;
  external?: boolean;
}

const allSidebarItems: SidebarItem[] = [
  {
    name: "Dashboard",
    path: "/dashboard",
    icon: Home,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "SUPER_ADMIN",
      "OPERATIONS_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
      "TRAINER",
      "TRAINEE",
      "CONTENT_ADMIN",
      "MOBILISATION_PARTNER",
    ],
    description: "Start here to explore every tool you have access to.",
  },
  {
    name: "Program Management",
    path: "/dashboard/program-management",
    icon: Book,
    allowed_roles: ["SYSTEM_ADMIN"],
    description: "Configure and monitor training programs across centres.",
  },
  {
    name: "Partners",
    path: "/dashboard/partners",
    icon: Handshake,
    adminOnly: true,
    allowed_roles: ["SYSTEM_ADMIN"],
    description: "Manage mobilisation partners and their onboarding details.",
  },
  {
    name: "Batches",
    path: "/dashboard/batches",
    icon: Building2,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "SUPER_ADMIN",
      "OPERATIONS_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
    ],
    description: "Create, update, and review active and upcoming batches.",
  },
  {
    name: "Batch Consumption",
    path: "/dashboard/batch-consumption",
    icon: Activity,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "SUPER_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
      "TRAINER",
    ],
    description: "Pull batch consumption details from the Knowlens LMS.",
  },
  {
    name: "Batch Calendar",
    path: "/dashboard/batch-calendar",
    icon: Calendar,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "OPERATIONS_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
      "TRAINER",
      "TRAINEE",
    ],
    description: "View time-table and schedules for batches and events.",
  },
  {
    name: "LMS Portal",
    path: "/dashboard/lms-portal",
    icon: GraduationCap,
    allowed_roles: ["TRAINEE", "SYSTEM_ADMIN", "OPERATIONS_ADMIN", "TRAINER"],
    description: "Launch the Knowlens LMS without re-entering your credentials.",
  },
  {
    name: "Learning Path",
    path: "/dashboard/learning-path",
    icon: Route,
    allowed_roles: ["TRAINEE"],
    description: "See your training trail, milestones, and Knowlens progress.",
  },
  {
    name: "Attendance",
    path: "/dashboard/attendance",
    icon: Check,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "OPERATIONS_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
      "TRAINER",
      "TRAINEE",
    ],
    description: "Mark attendance, review submissions, and track presence.",
  },
  {
    name: "OJT Scorecards",
    path: "/dashboard/ojt-scorecards",
    icon: ClipboardList,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "OPERATIONS_ADMIN",
      "SUPER_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
      "TRAINER",
    ],
    description: "Download templates and upload trainer OJT evaluations for each batch.",
  },
  {
    name: "Reports",
    path: "/dashboard/reports",
    icon: BarChart3,
    adminOnly: true,
    allowed_roles: ["SYSTEM_ADMIN"],
    description: "Access organisation-wide analytics and performance metrics.",
  },
  {
    name: "Assessments Admin",
    path: "/dashboard/assessments-admin",
    icon: FileText,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
      "TRAINER",
    ],
    description: "Set up, assign, and monitor assessments for batches.",
  },
  {
    name: "Mobilisations",
    path: "/dashboard/mobilisations",
    icon: Briefcase,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "OPERATIONS_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
    ],
    description: "Track mobilisation pipeline and candidate onboarding status.",
  },
  {
    name: "Centers",
    path: "/dashboard/centers",
    icon: Building,
    allowed_roles: ["SYSTEM_ADMIN", "MOBILISATION_PARTNER_ADMIN"],
    description: "Maintain centre details, capacity, and compliance records.",
  },
  {
    name: "Invoices",
    path: "/dashboard/invoices",
    icon: Receipt,
    allowed_roles: ["SYSTEM_ADMIN", "OPERATIONS_ADMIN", "MOBILISATION_PARTNER_ADMIN"],
    description: "Generate and approve invoices tied to project milestones.",
  },
  {
    name: "Trainers",
    path: "/dashboard/trainers",
    icon: User,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "TRAINER",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
    ],
    description: "Review trainer profiles, assignments, and progress.",
  },
  {
    name: "Trainees",
    path: "/dashboard/trainees",
    icon: GraduationCap,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
      "TRAINER",
      "TRAINEE",
    ],
    description: "Manage trainee records, enrolments, and status updates.",
  },
  {
    name: "Take Assessments",
    path: "/dashboard/assessments",
    icon: BadgeQuestionMark,
    allowed_roles: ["TRAINEE"],
    description: "Attempt the assessments assigned to you.",
  },
  {
    name: "Certificates",
    path: "/dashboard/certificates",
    icon: Medal,
    allowed_roles: ["SYSTEM_ADMIN"],
    description: "Issue and manage certificates across completed batches.",
  },
  {
    name: "Placements",
    path: "/dashboard/placements",
    icon: Briefcase,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
      "TRAINEE",
    ],
    description: "Track placement drives, offers, and candidate outcomes.",
  },
  {
    name: "Post-Placement Tracking",
    path: "/dashboard/post-placement-tracking",
    icon: ClipboardList,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "OPERATIONS_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
    ],
    description: "Monitor post-placement engagement and retention insights.",
  },
  {
    name: "My Certificates",
    path: "/dashboard/my-certificates",
    icon: Medal,
    allowed_roles: ["TRAINEE"],
    description: "Download certificates you have earned.",
  },
  {
    name: "Inquiries & Support",
    path: "/dashboard/inquiries",
    icon: MessageCircle,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
      "TRAINEE",
    ],
    description: "Raise and track support tickets and learner inquiries.",
  },
  // { name: 'Reports', path: '/dashboard/reports', icon: BarChart3,adminOnly: true },
  // { name: 'Settings', path: '/dashboard/settings', icon: Settings,adminOnly: true },
  {
    name: "Trainees Registration",
    path: "/dashboard/trainees-registration",
    icon: UploadCloud,
    allowed_roles: [
      "SYSTEM_ADMIN",
      "MOBILISATION_PARTNER_ADMIN",
      "CENTER_ADMIN",
      "PROJECT_MANAGER",
    ],
    description: "Register new trainees and upload onboarding documents.",
  },
  {
    name: "User Management",
    path: "/dashboard/users",
    icon: Users,
    allowed_roles: ["SUPER_ADMIN", "SYSTEM_ADMIN", "MOBILISATION_PARTNER_ADMIN"],
    description: "Create, edit, and deactivate application users.",
  },
  {
    name: "Audit Logs",
    path: "/dashboard/audit-logs",
    icon: ClipboardList,
    allowed_roles: ["SUPER_ADMIN"],
    description: "Review detailed system activity and change history.",
  },
  {
    name: "Queues (BullMQ)",
    path: `${BACKEND_BASE}/admin/queues`,
    icon: Activity,
    allowed_roles: ["SUPER_ADMIN", "SYSTEM_ADMIN"],
    description: "Monitor background jobs and retries.",
    external: true,
  },
];

const DashboardLayout: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [trainerProfile, setTrainerProfile] = useState<Trainer | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  useEffect(() => {
    if (user?.role !== "TRAINER") {
      setTrainerProfile(null);
      return;
    }
    const fetchProfile = async () => {
      try {
        setProfileLoading(true);
        const response = await trainerApi.getSelfProfile();
        if (response.success && response.data) {
          setTrainerProfile(response.data);
        }
      } catch (err) {
        console.error("Failed to load trainer profile", err);
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfile();
  }, [user?.role, location.pathname]);

  // Filter sidebar items based on user role
  const needsTrainerOnboarding =
    user?.role === "TRAINER" &&
    trainerProfile &&
    trainerProfile.status !== "Completed";

  const sidebarItems = useMemo(() => {
    if (!user?.role) return [];

    const baseItems = allSidebarItems.filter((item) => {
      if (!item.allowed_roles?.includes(user.role)) {
        return false;
      }
      return true;
    });

    if (user.role === "TRAINER") {
      const onboardingItem: SidebarItem = {
        name: "Onboarding",
        path: "/dashboard/trainer-onboarding",
        icon: FileText,
        allowed_roles: ["TRAINER"],
        description: "Complete the onboarding steps to unlock other modules.",
      };

      if (needsTrainerOnboarding) {
        return [onboardingItem];
      }

      // Already completed onboarding: hide the onboarding shortcut
      return baseItems;
    }

    if (user.role === "TRAINEE") {
      return baseItems.map((item) => {
        if (item.path !== "/dashboard/trainees") {
          return item;
        }
        return {
          ...item,
          name: "My Profile",
          description: "Review and update your trainee profile details.",
        };
      });
    }

    return baseItems;
  }, [user?.role, needsTrainerOnboarding]);

  if (user?.role === "TRAINER" && profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (needsTrainerOnboarding && location.pathname !== "/dashboard/trainer-onboarding") {
    return <Navigate to="/dashboard/trainer-onboarding" replace />;
  }

  // console.log('DashboardLayout - User:', user);
  // console.log('DashboardLayout - Filtered sidebar items:', sidebarItems.map(item => item.name));

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`
    fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
    ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
    ${sidebarCollapsed ? "lg:w-20" : "lg:w-64"}
  `}
      >
        <div className="flex h-full flex-col overflow-hidden">
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center">
                  <img
                    src={logoImage}
                    alt="Bajaj Finserv Logo"
                    className="h-8 w-auto object-contain"
                  />
                </div>
              </div>
              {!sidebarCollapsed && (
                <div className="ml-2">
                  <h1 className="text-base font-semibold text-gray-900 leading-tight">
                    Bajaj SkillServ <br></br> Centre Management
                  </h1>
                </div>
              )}
            </div>
            <button
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              className="hidden lg:flex p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="mt-5 flex-1 overflow-y-auto px-2 space-y-1 min-h-0 scrollbar-hide">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <a
                  key={item.name}
                  href={item.path}
                  target={item.external ? "_self" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  className={`
                  group flex items-center ${sidebarCollapsed ? "justify-center px-2" : "px-2"} py-2 text-sm font-medium rounded-md transition-colors duration-150
                  ${
                    isActive
                      ? "bg-primary-50 text-primary-700 border-r-4 border-primary-500"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }
                `}
                  onClick={(e) => {
                    e.preventDefault();
                    if (item.external) {
                      setAuthCookieForBackend();
                      const targetUrl = buildQueuesUrlWithToken(item.path);
                      window.location.assign(targetUrl);
                    } else {
                      window.history.pushState({}, "", item.path);
                      window.dispatchEvent(new PopStateEvent("popstate"));
                      setSidebarOpen(false);
                    }
                  }}
                >
                  <Icon
                    className={`${sidebarCollapsed ? "mr-0" : "mr-3"} h-5 w-5 ${
                      isActive
                        ? "text-primary-500"
                        : "text-gray-400 group-hover:text-gray-500"
                    }`}
                  />
                  {!sidebarCollapsed && (
                    <>
                      {item.name}
                      {item.badge && (
                        <span className="ml-auto bg-red-100 text-red-600 text-xs font-medium px-2.5 py-0.5 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </a>
              );
            })}
          </nav>

          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className={`flex items-center w-full ${sidebarCollapsed ? "justify-center" : ""}`}>
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-gray-600" />
                </div>
              </div>
              {!sidebarCollapsed && (
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.full_name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user?.role}</p>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="ml-2 p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>

            <div className="flex-1 flex justify-between items-center lg:ml-0">
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  {sidebarItems.find((item) => item.path === location.pathname)
                    ?.name || "Dashboard"}
                </h1>
              </div>

              <div className="flex items-center space-x-4">
                <div className="hidden lg:block text-sm text-gray-500">
                  Welcome back, {user?.full_name}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Outlet context={{ sidebarItems }} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
